import { Component, Prop, Vue } from "vue-property-decorator";
import UlContentHeader from "@/components/UlContentHeader.vue";
import UlBreadcrumbs from "@/components/UlBreadcrumbs.vue";
import UIDataTable from "@/components/UIDataTable.vue";
import UIDialogDelete from "@/components/UlDialogDelete.vue";
import { DEFAULT_TABLE_OPTIONS, TableOptions } from "@/api/request";
import {
  DeleteRequest,
  SearchRequest,
  SortRequest
} from "@/api/menu-category/request";
import { SearchItem } from "@/api/menu-category/response";
import MenuCategorySearch from "@/store/menu-category/search";
import MenuCategoryModify from "@/store/menu-category/modify";
import Flash, { ErrorAlert, SuccessAlert } from "@/store/common/flash";
import Sortable, { MoveEvent } from "sortablejs";
import { GetRequest } from "@/api/menu-category-preview/request";
import MenuCategoryPreviewGet from "@/store/menu-category-preview/get";
import WindowOpen from "@/utils/window-open";
import ShopAgreementAPI from "@/store/shop-agreement/get";
import { GetRequest as GetAgreementRequest } from "@/api/shop-agreement/request";
import Admin from "@/store/admin/admin";

@Component({
  components: { UlContentHeader, UlBreadcrumbs, UIDataTable, UIDialogDelete }
})
export default class Category extends Vue {
  @Prop({ required: true })
  shopId!: number;

  // ------------
  // 固定値
  // ------------
  // タイトル
  headingMain = "メニュー管理";
  headingSub = "Menu management";
  breadCrumbs = [
    { text: "メニュー管理", disabled: true },
    {
      text: "店舗一覧",
      disabled: false,
      to: { name: "menu-management" }
    },
    { text: "メニューカテゴリ一覧", disabled: true }
  ];
  // テーブルヘッダ（UIDataTableコンポーネントに渡す）
  tableHeaders = [
    { text: "", always: true, value: "reorder", sortable: false },
    { text: "画像", value: "custom", sortable: false },
    { text: "タイトル", value: "title", sortable: false },
    { text: "公開フラグ", value: "viewFlg", sortable: false },
    { text: "メニュー登録数", value: "menuCount", sortable: false },
    {
      label: "メニュー一覧",
      text: "",
      value: "action",
      sortable: false
    },
    {
      label: "カテゴリ編集",
      text: "",
      value: "edit",
      sortable: false
    },
    {
      label: "削除",
      text: "",
      value: "delete",
      sortable: false
    }
  ];

  menuProps = {
    closeOnClick: false,
    closeOnContentClick: false,
    disableKeys: true,
    openOnClick: false,
    maxHeight: 304
  };

  // 契約状態
  agreements = {
    ap: false,
    line: false,
    web: false
  };

  // ------------
  // 変動値
  // ------------

  // 削除ダイアログの表示有無
  showDialog = false;

  // 削除されるカテゴリタイトル
  deletingCategoryTitle = "";

  // 削除されるカテゴリID
  deletingCategoryId = "";

  // ローディングステータス
  isLoading = false;

  // テーブル検索オプション（UIDataTableコンポーネントに渡す）
  tableOptions = DEFAULT_TABLE_OPTIONS;

  // ------------

  /**
   * 新規作成権限の有無
   */
  get writeFlg() {
    return MenuCategorySearch.getWriteFlg;
  }

  /**
   * テーブルに表示するアイテムリスト（UIDataTableコンポーネントに渡す）
   */
  get tableItems() {
    return MenuCategorySearch.getItems;
  }

  /**
   * 総件数（UIDataTableコンポーネントに渡す）
   */
  get totalCount() {
    return MenuCategorySearch.getTotalCount;
  }

  /**
   * クエリパラメータから店舗名を返却する（必須）
   */
  get shopName() {
    if (this.$route.query.shopName) {
      return this.$route.query.shopName;
    } else {
      return "";
    }
  }

  /**
   * 契約が未登録かの判定
   * @return true:契約が 未登録 false:契約が 登録済
   */
  isNotAgreement(): boolean {
    const result = this.agreements.ap === false && this.agreements.line === false && this.agreements.web === false;
    return result;
  }

  /**
   * createdライフサイクルフック（UIDataTableコンポーネントに渡す）
   */
  async created() {
    // 契約情報を取得する
    if (this.shopId) {
      let requestAgrement = {
        shopId: String(this.shopId)
      } as GetAgreementRequest;
      await ShopAgreementAPI.get(requestAgrement);
      if (!ShopAgreementAPI.isSuccess) {
        await Flash.setErrorNow({
          message: "",
          showReloadButton: true
        } as ErrorAlert);
        return;
      } else {
        const AgreementItems = ShopAgreementAPI.getagreementsList;
        AgreementItems.forEach((item: { agreementsType: number }) => {
          if (item.agreementsType == 1) {
            this.agreements.ap = true;
          } else if (item.agreementsType == 2) {
            this.agreements.line = true;
          } else if (item.agreementsType == 3) {
            this.agreements.web = true;
          }
        });
      }
    } else {
      // スーパーユーザの場合は全て契約
      if (Admin.isAdmin) {
        this.agreements.ap = true;
        this.agreements.line = true;
        this.agreements.web = true;
      }
    }

    await MenuCategorySearch.restore({
      shopId: Number(this.shopId)
    } as SearchRequest);
    // 検索テーブルオプション
    const request = MenuCategorySearch.getSearchRequest;
    request.page && (this.tableOptions = request);

    await this.search();
  }

  /**
   * mountedライフサイクルフック（UIDataTableをソート可能にする）
   */
  mounted() {
    this.$nextTick(() => {
      const _self = this;

      const mainTable = document.querySelector(
        ".v-data-table tbody"
      ) as HTMLElement;
      Sortable.create(mainTable, {
        onEnd({ newIndex, oldIndex }) {
          const rowSelected = _self.tableItems.splice(oldIndex as number, 1)[0];
          _self.tableItems.splice(newIndex as number, 0, rowSelected);
          _self.sort().then();
        },
        onMove(evt: MoveEvent, originalEvent: Event): boolean {
          return (
            !evt.dragged.querySelector(".not-sortable") &&
            !evt.related.querySelector(".not-sortable")
          );
        }
      });
    });
  }

  /**
   * beforeDestroyライフサイクルフック
   */
  async beforeDestroy() {
    await MenuCategorySearch.clearResponse();
    await MenuCategoryModify.clearResponse();
  }

  /**
   * ページング変更コールバック
   *
   * @param tableOptions TableOptions
   */
  async tableChangeCallback(tableOptions: TableOptions) {
    // 子コンポーネントへpagingOptionsの変更が通知される
    this.tableOptions = tableOptions;
    await this.search();
  }

  /**
   * 検索ボタンがクリックされた際のコールバック
   */
  async searchClickCallback() {
    await Flash.clear();
    await this.search();
  }

  /**
   * Webプレビュー
   */
  async webPreview() {
    await MenuCategoryPreviewGet.get({
      shopId: this.shopId,
      isPreview: 2
    } as GetRequest);
    if (MenuCategoryPreviewGet.isSuccess) {
      WindowOpen.webPreview(MenuCategoryPreviewGet.getPreviewUrl);
    } else {
      await Flash.setErrorNow({
        message: MenuCategoryPreviewGet.getMessage,
        showReloadButton: false
      } as ErrorAlert);
    }
  }

  /**
   * APプレビュー
   */
  async preview() {
    await MenuCategoryPreviewGet.get({
      shopId: this.shopId,
      isPreview: 1
    } as GetRequest);
    if (MenuCategoryPreviewGet.isSuccess) {
      WindowOpen.preview(MenuCategoryPreviewGet.getPreviewUrl);
    } else {
      await Flash.setErrorNow({
        message: MenuCategoryPreviewGet.getMessage,
        showReloadButton: false
      } as ErrorAlert);
    }
  }

  /**
   * テーブル行のアクションボタンがクリックされた際のコールバック
   *
   * @param item 選択行のAdminItem
   */
  async actionClickCallback(item: SearchItem) {
    await this.$router.push({
      name: "menu-management-category-menu-list",
      params: { shopId: String(this.shopId), id: item.id },
      query: { shopName: this.shopName, category_Title: item.title }
    });
  }

  /**
   * テーブル行の編集ボタンがクリックされた際のコールバック
   *
   * @param item 選択行のAdminItem
   */
  async editClickCallback(item: SearchItem) {
    await this.$router.push({
      name: "menu-management-category-edit",
      params: { shopId: String(this.shopId), id: item.id },
      query: { shopName: this.shopName }
    });
  }

  /**
   * テーブル行の削除ボタンがクリックされた際のコールバック
   *
   * @param item 選択行のAdminItem
   */
  deleteClickCallback(item: SearchItem) {
    this.showDialog = true;
    this.deletingCategoryId = item.id;
    this.deletingCategoryTitle = item.title;
  }

  /**
   * ダイアログの削除のコールバック
   */
  async doDeleteOnDialog() {
    await Flash.clear();
    this.showDialog = false;
    if (!this.deletingCategoryId) {
      return;
    }

    this.isLoading = true;
    await MenuCategoryModify.deleteOne({
      id: Number(this.deletingCategoryId)
    } as DeleteRequest);
    this.isLoading = false;
    if (MenuCategoryModify.isSuccess) {
      await Flash.setSuccessNow({
        message: "メニューカテゴリを削除しました。",
        consumePath: ""
      } as SuccessAlert);
      await this.search();
    } else {
      await Flash.setErrorNow({
        message: MenuCategoryModify.getMessage,
        showReloadButton: false
      } as ErrorAlert);
    }
  }

  /**
   * 検索処理
   */
  async search() {
    this.isLoading = true;
    await MenuCategorySearch.search({
      shopId: Number(this.shopId)
    } as SearchRequest);
    if (!MenuCategorySearch.isSuccess) {
      await Flash.setErrorNow({
        message: MenuCategorySearch.getMessage,
        showReloadButton: true
      } as ErrorAlert);
    }
    this.isLoading = false;
  }

  /**
   * 並び替え処理
   */
  async sort() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    await MenuCategoryModify.sort(this.createSortRequest());
    if (!MenuCategoryModify.isSuccess) {
      await Flash.setErrorNow({
        message: MenuCategoryModify.getMessage,
        showReloadButton: false
      } as ErrorAlert);
    }
    this.isLoading = false;
  }

  goToNew() {
    this.$router
      .push({
        name: "menu-management-category-new",
        params: { shopId: String(this.shopId) },
        query: { shopName: this.shopName }
      })
      .then();
  }

  /**
   * dataから並び替え用のリクエストを作成する
   */
  private createSortRequest() {
    const request = { shopId: Number(this.shopId) } as SortRequest;
    request.ids = this.tableItems.map(i => Number(i.id));
    return request;
  }
}
